import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CompletedSummaryView } from '@/features/one-on-one/summary/CompletedSummaryView';
import { PlatformRestriction } from '@/shared/navigation/components/PlatformRestriction';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import {
  useGetEmployeeOneOnOnes,
  useGetManagerOneOnOnes,
  useGetOneOnOneById,
} from '@/shared/hooks/useOneOnOne';
import { useMemo, useEffect } from 'react';
import { OneOnOneStatus } from '@momentum/types';
import { Card, CardContent } from '@/shared/components/ui/Card';

export const Route = createFileRoute('/_dashboard/one-on-one/$id/completed')({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const employee = useEmployeeContext();
  const navigate = useNavigate();

  // Fetch the one-on-one to check its status
  const { data: oneOnOne, isLoading: isLoadingOneOnOne } =
    useGetOneOnOneById(id);

  const { data: employeeOneOnOnes } = useGetEmployeeOneOnOnes({
    employeeId: employee._id,
  });

  const { data: managerOneOnOnes } = useGetManagerOneOnOnes({
    managerId: employee._id,
  });

  const targetEmployeeId = useMemo(() => {
    // Check if this is an employee's one-on-one or manager's
    const employeeOneOnOne = employeeOneOnOnes?.meetings?.find(
      (item) => item._id === id
    );
    const managerOneOnOne = managerOneOnOnes?.find((item) => item._id === id);

    const currentOneOnOne = employeeOneOnOne || managerOneOnOne;

    // Determine which employee's data to show
    return currentOneOnOne?.employeeId || employee._id;
  }, [employeeOneOnOnes, managerOneOnOnes, id, employee._id]);

  // Redirect if the meeting is not completed
  useEffect(() => {
    if (oneOnOne && oneOnOne.status !== OneOnOneStatus.COMPLETED) {
      navigate({ to: `/one-on-one/${id}/goals-review` });
    }
  }, [oneOnOne, navigate, id]);

  if (isLoadingOneOnOne) {
    return (
      <PlatformRestriction notAllowedPlatforms={['mobile']}>
        <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200/60">
          <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8 space-y-8">
            <Card className="border-none bg-white/90 shadow-lg backdrop-blur">
              <CardContent className="flex items-center justify-center gap-3 py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-primary" />
                <p className="text-muted-foreground">Loading...</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </PlatformRestriction>
    );
  }

  // Only render if the meeting is completed
  if (!oneOnOne || oneOnOne.status !== OneOnOneStatus.COMPLETED) {
    return null;
  }

  return (
    <PlatformRestriction notAllowedPlatforms={['mobile']}>
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200/60">
        <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8 space-y-8">
          <CompletedSummaryView sessionId={id} employeeId={targetEmployeeId} />
        </div>
      </div>
    </PlatformRestriction>
  );
}
